import { useCallback, useMemo, useRef, useState } from 'react';
import type { Graph } from '../model';
import { cloneGraph, isOpError, OP_ERROR_TEXT, OpError, stableJson } from './graphOps';
import { completeLayout, Layout, placeNew, pruneLayout } from './geometry';
import { canRedo, canUndo, historyOf, History, push, redo, undo } from './history';
import { TRIGGER } from './ports';

/** What the editor edits: the graph (as the API takes it, without `layout`) and the node positions. */
export interface EdState {
  graph: Graph;
  layout: Layout;
}

export type EdResult = EdState | { error: OpError } | { message: string } | null;

/** The graph without positions (the API gets them separately: they are not part of the version's hash). */
export function stripLayout(g: Graph): Graph {
  const { layout: _layout, ...rest } = g;
  return rest as Graph;
}

/** Positions for every step: new steps get a spot near their predecessor, removed ones lose theirs. */
function syncLayout(next: EdState): EdState {
  const ids = [TRIGGER, ...Object.keys(next.graph.steps)];
  if (ids.every((id) => next.layout[id]) && Object.keys(next.layout).length === ids.length) return next;
  let layout = pruneLayout(next.graph, next.layout);
  for (const id of ids) if (!layout[id]) layout = { ...layout, [id]: placeNew(next.graph, layout, id) };
  return { graph: next.graph, layout };
}

/**
 * State of the journey editor (docs/pilot/SPEC.md §12.4): one undo/redo history of `{ graph, layout }` snapshots.
 * `apply` runs a pure operation (graphOps) and turns its refusal into the text shown to the person.
 */
export function useEditor() {
  const [h, setH] = useState<History<EdState> | null>(null);
  const ref = useRef<History<EdState> | null>(null);
  ref.current = h;

  /** A new starting point (a loaded version): no undo past it. */
  const reset = useCallback((graph: Graph, layout: Layout | null | undefined) => {
    const g = stripLayout(cloneGraph(graph));
    setH(historyOf({ graph: g, layout: completeLayout(g, layout ?? graph.layout) }));
  }, []);

  /** Replace the state as one undoable step (a loaded draft, the JSON tab). */
  const load = useCallback((graph: Graph, layout: Layout | null | undefined) => {
    const g = stripLayout(cloneGraph(graph));
    setH((cur) => (cur ? push(cur, { graph: g, layout: completeLayout(g, layout ?? graph.layout) }) : historyOf({ graph: g, layout: completeLayout(g, layout) })));
  }, []);

  /** Runs an operation on the current state; returns the error text or null. */
  const apply = useCallback((fn: (s: EdState) => EdResult, opts: { coalesce?: string; windowMs?: number } = {}): string | null => {
    const cur = ref.current;
    if (!cur) return null;
    const r = fn(cur.present);
    if (!r) return null;
    if (isOpError(r)) return OP_ERROR_TEXT[r.error];
    if ('message' in r) return r.message;
    const next = push(cur, syncLayout(r), opts);
    ref.current = next;
    setH(next);
    return null;
  }, []);

  /**
   * The step panel's way in: a mutation of a clone that carries `layout` (so renaming a step keeps its position).
   * Edits of one field within 1 s are one undo entry.
   */
  const patchGraph = useCallback(
    (fn: (g: Graph) => void, coalesce?: string) =>
      apply((s) => {
        const g = { ...cloneGraph(s.graph), layout: { ...s.layout } } as Graph;
        fn(g);
        const layout = (g.layout ?? s.layout) as Layout;
        return { graph: stripLayout(g), layout };
      }, { coalesce }),
    [apply],
  );

  const step = useCallback((fn: (x: History<EdState>) => History<EdState>) => {
    const cur = ref.current;
    if (!cur) return;
    const next = fn(cur);
    ref.current = next;
    setH(next);
  }, []);
  const doUndo = useCallback(() => step(undo), [step]);
  const doRedo = useCallback(() => step(redo), [step]);

  const state = h?.present ?? null;
  return useMemo(
    () => ({
      state,
      graph: state?.graph ?? null,
      layout: state?.layout ?? null,
      reset,
      load,
      apply,
      patchGraph,
      undo: doUndo,
      redo: doRedo,
      canUndo: !!h && canUndo(h),
      canRedo: !!h && canRedo(h),
    }),
    [state, h, reset, load, apply, patchGraph, doUndo, doRedo],
  );
}
export type Editor = ReturnType<typeof useEditor>;

/** Changed against a saved version? (positions and graph separately: only the graph needs a new version) */
export const sameGraph = (a: Graph | null | undefined, b: Graph | null | undefined) => !!a && !!b && stableJson(stripLayout(a)) === stableJson(stripLayout(b));
export const sameLayout = (a: Layout | null | undefined, b: Layout | null | undefined) => stableJson(a ?? {}) === stableJson(b ?? {});
